import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Settings, 
  Loader2, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  Mail, 
  User, 
  Shield, 
  Save, 
  Key,
  Check,
  Users,
  UserPlus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export const SettingsPage = () => {
  const { api, user } = useAuth();
  const [emailLoading, setEmailLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Email state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  
  // Password form state
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  // Super Admin management state
  const [superAdmins, setSuperAdmins] = useState([]);
  const [superAdminsLoading, setSuperAdminsLoading] = useState(true);
  const [addSuperAdminOpen, setAddSuperAdminOpen] = useState(false);
  const [superAdminForm, setSuperAdminForm] = useState({ username: '', password: '' });
  const [createLoading, setCreateLoading] = useState(false);

  // Load current email on mount
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  // Load super admins
  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  const fetchSuperAdmins = async () => {
    try {
      const response = await api.get('/users');
      const admins = response.data.filter(u => u.role === 'super_admin');
      setSuperAdmins(admins);
    } catch (error) {
      console.error('Error fetching super admins:', error);
    } finally {
      setSuperAdminsLoading(false);
    }
  };

  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    if (!superAdminForm.username || !superAdminForm.password) {
      toast.error('Compila tutti i campi');
      return;
    }
    setCreateLoading(true);
    try {
      await api.post('/auth/register', {
        username: superAdminForm.username,
        password: superAdminForm.password,
        role: 'super_admin',
        structure_id: null
      });
      toast.success('Super Admin creato');
      setAddSuperAdminOpen(false);
      setSuperAdminForm({ username: '', password: '' });
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteSuperAdmin = async (admin) => {
    if (admin.username === 'superadmin') {
      toast.error('Non puoi eliminare il Super Admin principale');
      return;
    }
    if (!window.confirm(`Sei sicuro di voler eliminare ${admin.username}?`)) return;
    
    try {
      await api.delete(`/users/${admin.id}`);
      toast.success('Super Admin eliminato');
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const validateEmail = (emailValue) => {
    if (!emailValue) return true;
    const emailRegex = /^[\w.-]+@[\w.-]+\.\w+$/;
    return emailRegex.test(emailValue);
  };

  const handleEmailSave = async () => {
    if (email && !validateEmail(email)) {
      setEmailError('Formato email non valido');
      return;
    }
    
    setEmailLoading(true);
    setEmailError('');
    
    try {
      await api.put('/auth/profile', { email: email || null });
      toast.success('Email aggiornata con successo');
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 2000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'aggiornamento';
      setEmailError(message);
      toast.error(message);
    } finally {
      setEmailLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'La password attuale è obbligatoria';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'La nuova password è obbligatoria';
    } else if (formData.newPassword.length < 4) {
      newErrors.newPassword = 'La nuova password deve avere almeno 4 caratteri';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Conferma la nuova password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Le password non coincidono';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });
      
      toast.success('Password aggiornata con successo');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante il cambio password';
      toast.error(message);
      
      if (message.includes('attuale')) {
        setErrors({ currentPassword: message });
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center text-white shadow-lg">
          <Settings className="w-7 h-7" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Impostazioni</h1>
          <p className="text-slate-500 text-sm">Gestisci il tuo account e le preferenze</p>
        </div>
      </div>

      {/* Profile Info Card */}
      <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-slate-50 to-white">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-sky-600 rounded-2xl flex items-center justify-center text-white shadow-md">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{user?.username || 'Super Admin'}</h2>
              <p className="text-slate-500 text-sm">Super Admin</p>
              {email && (
                <p className="text-slate-400 text-xs mt-1 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {email}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Section */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Email di Recupero
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                Usata per recuperare la password in caso di smarrimento
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Indirizzo Email
              </Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError('');
                    setEmailSaved(false);
                  }}
                  className={`flex-1 ${emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  placeholder="nome@esempio.com"
                  data-testid="email-input"
                />
                <Button
                  type="button"
                  onClick={handleEmailSave}
                  disabled={emailLoading}
                  className={`${emailSaved ? 'bg-emerald-500' : 'bg-emerald-600 hover:bg-emerald-700'} min-w-[44px]`}
                  data-testid="save-email-btn"
                >
                  {emailLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : emailSaved ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </Button>
              </div>
              {emailError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {emailError}
                </p>
              )}
              <p className="text-xs text-slate-400">
                Riceverai un link per reimpostare la password a questo indirizzo
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Sicurezza
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                Aggiorna la tua password di accesso
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-sm font-medium text-slate-700">
                Password Attuale
              </Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => handleChange('currentPassword', e.target.value)}
                  className={`pr-10 ${errors.currentPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  placeholder="Inserisci la password attuale"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.currentPassword}
                </p>
              )}
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">
                Nuova Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleChange('newPassword', e.target.value)}
                  className={`pr-10 ${errors.newPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  placeholder="Inserisci la nuova password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                Conferma Nuova Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className={`pr-10 ${errors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  placeholder="Conferma la nuova password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 mt-2"
              disabled={passwordLoading}
              data-testid="change-password-btn"
            >
              {passwordLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Aggiornamento...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Aggiorna Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Super Admin Management Section */}
      <Card className="border-purple-200 shadow-sm bg-gradient-to-br from-purple-50/50 to-white">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Super Admin
                </CardTitle>
                <CardDescription className="text-slate-500 text-sm">
                  {superAdmins.length} amministrator{superAdmins.length === 1 ? 'e' : 'i'} global{superAdmins.length === 1 ? 'e' : 'i'}
                </CardDescription>
              </div>
            </div>
            <Dialog open={addSuperAdminOpen} onOpenChange={setAddSuperAdminOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  data-testid="add-super-admin-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuovo Super Admin
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuovo Super Admin</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateSuperAdmin} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={superAdminForm.username}
                      onChange={(e) => setSuperAdminForm({ ...superAdminForm, username: e.target.value })}
                      placeholder="Username"
                      data-testid="new-super-admin-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={superAdminForm.password}
                      onChange={(e) => setSuperAdminForm({ ...superAdminForm, password: e.target.value })}
                      placeholder="Password"
                      data-testid="new-super-admin-password"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    disabled={createLoading}
                    data-testid="create-super-admin-btn"
                  >
                    {createLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4 mr-2" />
                    )}
                    Crea Super Admin
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>

        <CardContent>
          {superAdminsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="space-y-2">
              {superAdmins.map(admin => (
                <div 
                  key={admin.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100"
                  data-testid={`super-admin-item-${admin.username}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-sm font-bold text-purple-600">
                      {admin.username.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-slate-900">{admin.username}</span>
                    <Badge className="bg-purple-100 text-purple-700 text-xs">Super Admin</Badge>
                  </div>
                  {admin.username !== 'superadmin' && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteSuperAdmin(admin)}
                      data-testid={`delete-super-admin-${admin.username}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
